import type { FC } from 'react';

import { Loader2, Lock, Download, Check, Share2 } from 'lucide-react';

import { cn } from '@shared/utils/tailwind.utils';

interface DownloadAgentButtonProps {
  isLoading: boolean;
  isSuccess: boolean;
  isInteractionDisabled: boolean;
  handleDownload: () => void | Promise<void>;
}

export const DownloadAgentButton: FC<DownloadAgentButtonProps> = ({
  isLoading,
  isSuccess,
  isInteractionDisabled,
  handleDownload,
}) => {
  const isDisabled = isInteractionDisabled || isLoading || isSuccess;

  return (
    <button
      type="button"
      onClick={handleDownload}
      disabled={isDisabled}
      className="flex items-center gap-1.5 px-3 py-2 border border-gray-700 hover:border-gray-500 rounded bg-transparent text-gray-200 hover:text-white text-xs font-medium transition cursor-pointer disabled:cursor-not-allowed disabled:opacity-50"
    >
      {isLoading && (
        <>
          <Loader2 className="h-4 w-4 animate-spin" />
          <span>Downloading...</span>
        </>
      )}

      {!isLoading && isSuccess && (
        <>
          <Lock size={14} />
          <span>Downloaded</span>
        </>
      )}

      {!isLoading && !isSuccess && (
        <>
          {!isInteractionDisabled && <Download size={14} />}
          <span>Download</span>
        </>
      )}
    </button>
  );
};

interface ShareAgentButtonProps {
  isSuccess: boolean;
  showCopiedText: boolean;
  isInteractionDisabled: boolean;
  handleShare: () => void | Promise<void>;
}

export const ShareAgentButton: FC<ShareAgentButtonProps> = ({
  isSuccess,
  showCopiedText,
  isInteractionDisabled,
  handleShare,
}) => {
  const isDisabled = isInteractionDisabled || showCopiedText;

  return (
    <button
      type="button"
      onClick={handleShare}
      disabled={isDisabled}
      className={cn(
        'flex items-center gap-1.5 px-3 py-2 border rounded bg-transparent text-gray-200 hover:text-white text-xs font-medium transition cursor-pointer disabled:cursor-not-allowed disabled:opacity-50',
        showCopiedText
          ? 'border-emerald-500/30 bg-emerald-950/10 text-emerald-400 hover:text-emerald-300'
          : 'border-gray-700 hover:border-gray-500',
      )}
    >
      {isSuccess && <Lock size={14} />}
      {showCopiedText && <Check size={14} className="text-green-500" />}
      {!isSuccess && !showCopiedText && <Share2 size={14} />}

      <span>{showCopiedText ? 'Copied!' : 'Share'}</span>
    </button>
  );
};
